/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback } from 'react';

import { DescriptionPage_UpsellType as UpsellTypes } from '__generated__/graphql-types';

import type UserAgent from 'js/lib/useragent';

import { Tab, TabContext, TabPanel, Tabs, Typography, Typography2 } from '@coursera/cds-core';

import { getUpsellCopy } from 'bundles/stackability-common/stackability-common-index';
import StackabilityCarousel from 'bundles/xddp/components/homepage/StackabilityCarousel';
import type { UpsellsByDegreeSlug } from 'bundles/xddp/types';

import _t from 'i18n!nls/xddp';

const styles = {
  sectionHeader: css`
    padding-bottom: 26px;
    font-size: 28px;
  `,
  tabButton: css`
    background: transparent !important;
    border: none !important;
    padding: 20px 8px !important;
  `,
  textWrap: css`
    display: flex;
    flex-direction: column;
  `,
  gap: css`
    flex-grow: 1;
  `,
};

type Props = {
  items: UpsellsByDegreeSlug;
  userAgent: UserAgent;
};

type TabContentProps = {
  items: UpsellsByDegreeSlug;
  text: string;
  userAgent: UserAgent;
  disclaimer?: string;
};

const TabContent = ({ items, text, disclaimer, userAgent }: TabContentProps) => {
  return (
    <div css={styles.textWrap}>
      <Typography2 component="p" variant="bodyPrimary" className="p-b-3 p-t-2">
        {text}
      </Typography2>
      {disclaimer && (
        <>
          <div css={styles.gap} />
          <Typography2 component="p" variant="bodyPrimary" className="p-b-3">
            {disclaimer}
          </Typography2>
        </>
      )}
      <StackabilityCarousel items={items} userAgent={userAgent} />
    </div>
  );
};

const MultipleStackability = ({ items, userAgent }: Props) => {
  const partOfItems = items?.filter((item) => item.upsellType === UpsellTypes.PartOf) || [];
  const offersCreditItems = items?.filter((item) => item.upsellType === UpsellTypes.OffersCredit) || [];
  const relatedToItems = items?.filter((item) => item.upsellType === UpsellTypes.RelatedTo) || [];

  const upsellCopy = getUpsellCopy().degreesConversion.degreeDescription;
  const upsellLabelStrings = () => ({
    [UpsellTypes.PartOf]: upsellCopy.partOfCopy.tabHeader,
    [UpsellTypes.OffersCredit]: upsellCopy.offersCreditCopy.tabHeader,
    [UpsellTypes.RelatedTo]: upsellCopy.relatedToCopy.tabHeader,
  });

  const getInitialTab = useCallback<() => $TSFixMe>((): string => {
    if (partOfItems.length) {
      return UpsellTypes.PartOf;
    } else if (offersCreditItems.length) {
      return UpsellTypes.OffersCredit;
    } else if (relatedToItems.length) {
      return UpsellTypes.RelatedTo;
    } else {
      return UpsellTypes.PartOf;
    }
  }, [offersCreditItems?.length, partOfItems?.length, relatedToItems?.length]);

  const [currentTab, setCurrentTab] = React.useState<string>(getInitialTab());
  const upsellLabels = upsellLabelStrings();

  return (
    <div data-testid="multiple-stackability-section">
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2" css={styles.sectionHeader}>
        {upsellCopy.header}
      </Typography>
      {currentTab === UpsellTypes.PartOf && (
        <React.Fragment>
          <Typography2 component="p" variant="bodyPrimary" className="p-b-3">
            {upsellCopy.partOfCopy.subHeader}
          </Typography2>
        </React.Fragment>
      )}
      {currentTab === UpsellTypes.OffersCredit && (
        <React.Fragment>
          <Typography2 component="p" variant="bodyPrimary" className="p-b-3">
            {upsellCopy.offersCreditCopy.subHeader}
          </Typography2>
        </React.Fragment>
      )}
      {currentTab === UpsellTypes.RelatedTo && (
        <React.Fragment>
          <Typography2 component="p" variant="bodyPrimary" className="p-b-3">
            {upsellCopy.relatedToCopy.subHeader}
          </Typography2>
        </React.Fragment>
      )}
      <TabContext value={currentTab}>
        <Tabs aria-label={_t('Stackable Specialization Tabs')}>
          {partOfItems.length ? (
            <Tab
              label={upsellLabels[UpsellTypes.PartOf]}
              value={UpsellTypes.PartOf}
              onClick={() => setCurrentTab(UpsellTypes.PartOf)}
              variant="section"
              css={styles.tabButton}
            />
          ) : (
            <div aria-hidden={true} />
          )}
          {offersCreditItems.length ? (
            <Tab
              label={upsellLabels[UpsellTypes.OffersCredit]}
              value={UpsellTypes.OffersCredit}
              onClick={() => setCurrentTab(UpsellTypes.OffersCredit)}
              variant="section"
              css={styles.tabButton}
            />
          ) : (
            <div aria-hidden={true} />
          )}
          {relatedToItems.length ? (
            <Tab
              label={upsellLabels[UpsellTypes.RelatedTo]}
              value={UpsellTypes.RelatedTo}
              onClick={() => setCurrentTab(UpsellTypes.RelatedTo)}
              variant="section"
              css={styles.tabButton}
            />
          ) : (
            <div aria-hidden={true} />
          )}
        </Tabs>

        <TabPanel value={UpsellTypes.PartOf}>
          <TabContent items={partOfItems} text={upsellCopy.partOfCopy.bodyText} userAgent={userAgent} />
        </TabPanel>

        <TabPanel value={UpsellTypes.OffersCredit}>
          <TabContent
            items={offersCreditItems}
            text={upsellCopy.offersCreditCopy.bodyText}
            disclaimer={upsellCopy.offersCreditCopy.disclaimer}
            userAgent={userAgent}
          />
        </TabPanel>

        <TabPanel value={UpsellTypes.RelatedTo}>
          <TabContent items={relatedToItems} text={upsellCopy.relatedToCopy.bodyText} userAgent={userAgent} />
        </TabPanel>
      </TabContext>
    </div>
  );
};

export default MultipleStackability;
